import React from 'react';
import { FiBriefcase, FiTarget, FiCheckCircle, FiUser, FiCalendar, FiDollarSign } from 'react-icons/fi';

const OverviewTab = ({ insightData, Section }) => {
  if (!insightData?.executive_intelligence_summary) return null;

  const execSummary = insightData.executive_intelligence_summary;

  return (
    <>
      {/* Executive Intelligence Summary */}
      <Section title="Executive Intelligence Summary" iconComponent={FiBriefcase}>
        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6">
          {/* Company header with status indicators */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <FiBriefcase className="text-gray-900 w-5 h-5" />
              <span className="font-bold text-gray-900 text-lg">
                {execSummary.account_status_vector?.company_name}
              </span>
            </div>
            <div className="flex gap-3 flex-wrap">
              <span className="bg-blue-50 text-blue-800 border border-blue-200 px-3 py-2 rounded-full text-sm font-semibold">
                Health {execSummary.account_status_vector?.health_score}
              </span>
              <span className="bg-orange-50 text-orange-800 border border-orange-200 px-3 py-2 rounded-full text-sm font-semibold">
                Risk {execSummary.account_status_vector?.risk_level}
              </span>
              <span className="bg-green-50 text-green-800 border border-green-200 px-3 py-2 rounded-full text-sm font-semibold">
                Momentum {execSummary.account_status_vector?.momentum_direction}
              </span>
            </div>
          </div>

          {/* Opportunity Value */}
          {execSummary.account_status_vector?.opportunity_value && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <FiDollarSign className="text-green-600" />
                <span className="font-semibold text-green-600 text-sm">OPPORTUNITY VALUE</span>
              </div>
              <div className="text-sm text-green-800 font-semibold">
                {execSummary.account_status_vector.opportunity_value}
              </div>
            </div>
          )}

          {/* Primary Recommendation */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 mb-2">
              <FiTarget className="text-gray-700 w-4 h-4" />
              <span className="font-semibold text-gray-900 text-sm">Primary Recommendation</span>
            </div>
            <div className="text-sm text-gray-700 leading-relaxed">
              {execSummary.primary_recommendation}
            </div>
          </div>

          {/* Critical Actions Required */}
          {Array.isArray(execSummary.critical_actions_required) && (
            <div>
              <div className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FiCheckCircle className="text-green-600" />
                Critical Actions Required
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {execSummary.critical_actions_required.map((action, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-white shadow-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        action.priority === 'critical' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
                      }`}>
                        {action.priority?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 leading-relaxed mb-4">
                      {action.action}
                    </div>
                    <div className="flex flex-col gap-2 text-xs text-gray-500">
                      <div className="flex items-center gap-2">
                        <FiUser className="w-3 h-3" />
                        <span className="font-medium">{action.owner}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <FiCalendar className="w-3 h-3" />
                        <span>{action.deadline}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Section>

      {/* Intelligence Confidence */}
      {execSummary.intelligence_confidence && (
        <Section title="Intelligence Confidence" iconComponent={FiCheckCircle}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className={`px-4 py-2 rounded-full text-xs font-semibold uppercase ${
                execSummary.intelligence_confidence.level === 'high' ? 'bg-green-100 text-green-800' : 
                execSummary.intelligence_confidence.level === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'
              }`}>
                {execSummary.intelligence_confidence.level} Confidence
              </div>
            </div>
            
            {execSummary.intelligence_confidence.supporting_factors && (
              <div>
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Supporting Factors:
                </div>
                <ul className="list-disc list-inside space-y-1">
                  {execSummary.intelligence_confidence.supporting_factors.map((factor, idx) => (
                    <li key={idx} className="text-xs text-gray-600">
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>
      )}
    </>
  );
};

export default OverviewTab;
